<template>
  <div class="edit-anchor-access-container">
    <affix :offset="0">
      <div class="container" v-if="!loading">
        <ToolbarWrapper>
          <router-link
            :to="{name: 'AnchorPage', params: { uuid: uuid }}"
            class="btn btn-primary"
            target="_blank">View</router-link>
          <button
            class="btn btn-success"
            :disabled="loading"
            @click="togglePublish(true)"
            v-if="!common.published">Publish</button>
          <button
            class="btn btn-warning"
            :disabled="loading"
            @click="togglePublish(false)"
            v-if="common.published">Unpublish</button>
          <span class="text-muted">
            <template v-if="common.published">Last published {{ common.published_at }}</template>
            <template v-if="(!draft || !common.published) && saving"><i class="fa fa-circle-o-notch fa-spin"/> <i>Saving...</i></template>
            <template v-if="!common.published && !saving">Last saved {{ mostRecentUpdatedAt }}</template>
          </span>
          <div v-if="draft" class="draft-section">
            <DraftActions
              @savedraft="publishDraft()"
              @discarddraft="deleteDraft()"
              :uuid="uuid"
              class="draft-actions"/>
            <span class="text-muted">
              <template v-if="saving"><i class="fa fa-circle-o-notch fa-spin"/> <i>Saving...</i></template>
              <template v-else>Last saved {{ mostRecentUpdatedAt }}</template>
            </span>
          </div>
        </ToolbarWrapper>
      </div>
    </affix>
    <main class="container">
      <h1 class="sr-only">Edit Anchor Access Points</h1>
      <p class="text-muted" v-if="loading">
        <i class="fa fa-circle-o-notch fa-spin"/> Loading...
      </p>
      <div class="row" v-if="!loading">
        <div class="col-sm-4">
          <h2 class="no-margin-top">Subjects and Domains</h2>
          <SelectSubjects
            :subject-alignments.sync="common.subject_alignments"
            @update:subjectAlignments="saveCommonData({subject_alignments: $event})"
            :subjects="subjectsList"
            :subject-domains-map="subjectDomainsMap"
          />
          <SelectGrades
            :disabled="!common.subject_alignments || common.subject_alignments.length === 0"
            :options="common.subject_alignments && common.subject_alignments[0] && subjectGradesMap[common.subject_alignments[0].subject] || []"
            :selected.sync="common.grades"
            @update:selected="saveCommonData({grades: $event})" />

          <SelectCharacteristics
            :selected.sync="common.characteristics"
            @update:selected="saveCommonData({characteristics: $event})"
            :characteristics-map="characteristicsMap"
            :id="`${common.uuid}-characteristics-typeahead`"
            @add="populateCharacteristicsMap"/>

          <h2>Standards</h2>
          <div class="checkbox">
            <label for="universal-checkbox">
              <input
                type="checkbox"
                id="universal-checkbox"
                @change="saveCommonData({universal: common.universal})"
                v-model="common.universal"> Mark as Universal
            </label>
          </div>

          <SelectStandards
            v-for="setCode of validStandardSets"
            :key="setCode"
            :set-code="setCode"
            :selected="standardsForSet[setCode]"
            :standards-map="standardsMap"
            @update:selected="updateStandardsForSet(setCode, $event)"
            :id="`${setCode}-std-typeahead`" />


        </div>
        <div class="col-sm-8">
          <EditTitle v-model="common.title" @input="saveCommonData({title: $event})" />
          <div class="row">
            <div class="col-xs-6">
              <EditScore v-model="common.score" @input="saveCommonData({score: $event})" />
            </div>
            <div class="col-xs-6">
              <SelectStrategies
                :id="`${common.uuid}-select-strategies`"
                :selected.sync="common.adaptation_strategies"
                @update:selected="saveCommonData({adaptation_strategies: $event})"
                :strategies-map="strategiesMap"
                @add="populateStrategiesMap" />
            </div>
          </div>

          <h2>Access Points</h2>
          <p class="text-muted">Embed a link to a strategy with the following wiki syntax: <code>[[ strategy-name | text-to-show ]], e.g. [[Animations|animated models]]</code>. Include links to other websites with the following wiki syntax: <code>[[ https://goalbook.com/toolkit | text to display ]]</code>.</p>

          <EditAccessPoint
            v-for="ap of accessPoints"
            :level="ap.access_point_level"
            :key="ap.uuid"
            :uuid="ap.uuid"
            :text.sync="ap.goal_text"
            @update:text="pushUpdate({uuids: [ap.uuid], updates: {goal_text: $event}})">
            <SelectResources
              :ap-level="ap.access_point_level"
              :goal-type="'annual_target'"
              :id="ap.uuid"
              :selected.sync="ap.goal_resources"
              @update:selected="($event, $languages) => pushUpdate({uuids: [ap.uuid], updates: {goal_resources: $event, resource_languages: $languages}})"
              @add="($event, $languages) => populateResourcesMap() && addLanguage($event, ap.uuid, $languages)"
              :resources-map="resourcesMap" />

            <template>
              <h4 class="quarterly-objectives-header">
                Quarterly Objectives | {{ accessPointToLabelMap[ap.access_point_level] }}
                <button
                  class="btn btn-default"
                  v-if="!ap.objectives || ap.objectives.length === 0"
                  @click="generateObjectivesForAccessPoint(ap)">Generate quarterly objectives</button>
                <button
                  class="btn btn-default"
                  v-else
                  @click="clearObjectivesForAccessPoint(ap)">Remove objectives</button>
              </h4>

              <EditObjective
                v-for="(obj, index) of ap.objectives"
                :key="obj.level"
                :goal-uuid="ap.uuid"
                :level="obj.level"
                :ap-level="ap.access_point_level"
                :title.sync="obj.title"
                :text.sync="obj.text"
                @update:title="pushUpdate({uuids: [ap.uuid], updates: { objectives: formatObjectivesForUpdate(ap.objectives) }})"
                @update:text="pushUpdate({uuids: [ap.uuid], updates: { objectives: formatObjectivesForUpdate(ap.objectives) }})">
                <SelectResources
                  :id="`${ap.uuid}-${obj.level}-resources`"
                  :obj-level="obj.level"
                  :ap-level="ap.access_point_level"
                  :goal-type="'quarterly_objective'"
                  :selected.sync="obj.resources"
                  @update:selected="pushUpdate({uuids: [ap.uuid], updates: { objectives: formatObjectivesForUpdate(ap.objectives) }})"
                  @add="populateResourcesMap"
                  :resources-map="resourcesMap" />
                <hr v-if="index !== ap.objectives.length - 1">
              </EditObjective>

              <!-- Commenting this out since we don't need resources aligned to objectives. But if in the future we do, then we can uncomment this block. -->
              <!--
                <tabs v-if="false && ap.objectives && ap.objectives.length > 0">
                  <tab
                    v-for="obj of ap.objectives"
                    :key="obj.level"
                    :title="obj.level"
                    class="objective-tab">
                    <EditObjective
                      :goal-uuid="ap.uuid"
                      :level="obj.level"
                      :title.sync="obj.title"
                      :text.sync="obj.text"
                      @update:title="pushUpdate({uuids: [ap.uuid], updates: { objectives: ap.objectives }})"
                      @update:text="pushUpdate({uuids: [ap.uuid], updates: { objectives: ap.objectives }})">
                    <SelectAssessmentItemsInput
                      :id="`${ap.uuid}-${obj.level}-items`"
                      :selected-items-read-only="ap.assessment_items[obj.level]"
                      :async-validate-function="fetchItem"
                      @add="linkItemForAccessPointObjective(ap, obj, $event)"
                      @remove="unlinkItemForAccessPointObjective(ap, obj)" />
                    <SelectResources
                      :id="`${ap.uuid}-${obj.level}-resources`"
                      :selected.sync="ap.objective_resources[obj.level]"
                      @update:selected="pushUpdate({uuids: [ap.uuid], updates: {objective_resources: ap.objective_resources}})"
                      @add="populateResourcesMap"
                      :resources-map="resourcesMap" />
                    </EditObjective>
                  </tab>
                </tabs>
                -->
            </template>
          </EditAccessPoint>

          <button
            v-if="areAccessPointsMissing"
            class="btn btn-primary"
            @click="generatePlaceholderAccessPoints">Generate missing access points</button>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { Notification, Affix, MessageBox } from 'uiv'
import ToolbarWrapper from './components/ToolbarWrapper'
import DraftActions from './components/accesspoints/DraftActions'
import SelectSubjects from './components/accesspoints/SelectSubjects'
import SelectGrades from './components/accesspoints/SelectGrades'
import EditTitle from './components/accesspoints/EditTitle'
import EditScore from './components/accesspoints/EditScore'
import EditAccessPoint from './components/accesspoints/EditAccessPoint'
import SelectResources from './components/accesspoints/SelectResources'
import SelectStrategies from './components/accesspoints/SelectStrategies'
import SelectCharacteristics from './components/accesspoints/SelectCharacteristics'
import SelectStandards from './components/accesspoints/SelectStandards'
import EditObjective from './components/accesspoints/EditObjective'
import SelectAssessmentItemsInput from './components/accesspoints/SelectAssessmentItemsInput'
import { getAnchorPageForEditing, update, generateAccessPoints, checkAnchorPageForDraft, publishDraftForAnchorPage, deleteDraftForAnchorPage } from '@/admin/api/anchorpages'
import { getSubjectsDomainsGrades } from '@/admin/api/subjects'
import { getResources, getValidStandardSets, getStandardsQuery } from '@/api/pathways'
import { getStrategiesForUuids, getCharacteristicsForUuids } from '@/api/strategies'
import { toolkitUrls } from '@/links'
import itemMixin from '@/components/goals/common/item_mixin'
import { AccessPointsUtil } from '@/components/goals/common/access_points'
import { ObjectiveLevelValues } from '@/components/goals/common/objectives'
import Vue from 'vue'
import moment from 'moment'

const bufferedSaveMixin = {
  data() {
    return {
      saving: false,
      saveEvents: new Vue(),
      autosaveDelaySeconds: 2,
      autosaveTimeout: null,
      minSaveIndicatorDurationSeconds: 1,
      bufferedUpdates: [], // Ordered array of { uuids, updates }
      flushUpdatesCallbacks: []
    }
  },
  methods: {
    async addLanguage(resourceUuid, apUuid, languages) {
      if (!this.resourcesMap.hasOwnProperty(resourceUuid)) {
        await this.flushUpdates()
      }
      if (!languages.includes(this.resourcesMap[resourceUuid]['language'])) {
        this.pushUpdate({uuids: [apUuid], updates: {resource_languages: [...languages, this.resourcesMap[resourceUuid]['language']]}})
      }
    },
    pushUpdate({ uuids, updates }, flushImmediately = false) {
      this.bufferedUpdates.push({ uuids, updates })
      if (this.autosaveTimeout) {
        clearTimeout(this.autosaveTimeout)
      }
      if (flushImmediately) {
        this.flushUpdates()
      } else {
        this.autosaveTimeout = setTimeout(
          () => this.flushUpdates(),
          this.autosaveDelaySeconds * 1000
        )
      }
    },
    async flushUpdates() {
      if (this.bufferedUpdates.length === 0) {
        return
      }

      let consolidatedUpdates = {} // string list of uuids -> updates
      this.bufferedUpdates.forEach(({ uuids, updates }) => {
        let key = uuids.join(',')
        if (!consolidatedUpdates[key]) {
          consolidatedUpdates[key] = {}
        }
        consolidatedUpdates[key] = { ...consolidatedUpdates[key], ...updates }
      })
      this.bufferedUpdates = []

      this.saving = true
      let promises = [
        new Promise(resolve =>
          setTimeout(() => resolve(), this.minSaveIndicatorDurationSeconds * 1000)
        )
      ]

      for (const uuidsStr in consolidatedUpdates) {
        let updates = consolidatedUpdates[uuidsStr]
        updates.author_id = this.common.author_id
        let uuids = uuidsStr.split(',')
        promises.push(
          update({
            gradeLevelUUID: this.uuid,
            uuidsToUpdate: uuids,
            updates
          }).then(
            resp => {
              this.saveEvents.$emit('success', resp)

              // check if any of the uuids were NOT updated
              if (uuids.some(u => !resp[u])) {
                throw `Some access points were not updated: ${resp}`
              }
            },
            err => {
              this.saveEvents.$emit('error', { uuids, updates, err })
            }
          )
        )
      }

      // no need to wrap with try/catch here because we have error handlers for each promise already
      await Promise.all(promises)
      if (this.flushUpdatesCallbacks && this.flushUpdatesCallbacks.length > 0) {
        this.flushUpdatesCallbacks.forEach(cb => cb())
      }
      this.saving = false
    }
  }
}

export default {
  name: 'EditAnchorAccessPoints',
  mixins: [bufferedSaveMixin, itemMixin],
  components: {
    Affix,
    ToolbarWrapper,
    SelectSubjects,
    SelectGrades,
    EditTitle,
    EditScore,
    EditAccessPoint,
    SelectResources,
    SelectStrategies,
    SelectCharacteristics,
    SelectStandards,
    EditObjective,
    SelectAssessmentItemsInput,
    DraftActions
  },
  props: {
    uuid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      subjectsList: [],
      subjectDomainsMap: {},
      subjectGradesMap: {},

      common: {
        author_id: '',
        title: '',
        subject_alignments: [],
        grades: [],
        adaptation_strategies: [],
        published: null,
        score: 0,
        standard_set_refs: [],
        universal: null,
        published_at: null,
        characteristics: []
      },

      standardsForSet: {},
      accessPoints: [],

      validStandardSets: [],
      standardsMap: {},
      resourcesMap: {},
      strategiesMap: {},
      characteristicsMap: {},
      draft: false,
      accessPointToLabelMap: AccessPointsUtil.labelsMap,
    }
  },
  computed: {
    mostRecentUpdatedAt() {
      let updatedTimes = this.accessPoints.map(ap => ap.updated_at)
      updatedTimes.sort()
      return moment(new Date(updatedTimes.slice(-1)[0])).format('M/D/YY H:mm A')
    },
    areAccessPointsMissing() {
      return this.common.title && this.accessPoints.length < 3
    }
  },
  methods: {
    async init() {
      this.loading = true
      try {
        let ap = await getAnchorPageForEditing(this.uuid)

        // If this is not an access point, redirect to legacy edit page
        if (!ap.access_grade_level_uuid) {
          window.location.href = toolkitUrls.editorLinks.editGoal(ap.uuid)
          return
        }

        // if this is not the grade level UUID, redirect to grade level edit page
        if (ap.uuid !== ap.access_grade_level_uuid) {
          this.$router.replace({
            name: 'EditAnchorAccessPoints',
            params: { uuid: ap.access_grade_level_uuid }
          })
          return
        }

        // we know ap is the grade level access point, so it goes first
        this.accessPoints = [
          {
            access_point_level: ap.access_point_level,
            uuid: ap.uuid,
            author_id: ap.author_id,
            goal_text: ap.goal_text,
            goal_resources: ap.goal_resources,
            resource_languages: ap.resource_languages,
            objectives: ap.objectives,
            objective_resources: ap.objective_resources || {},
            assessment_items: ap.assessment_items || {},
            updated_at: ap.updated_at
          },
          ap.access_points.find(
            ap => ap.access_point_level === AccessPointsUtil.valuesMap.APPROACHING
          ),
          ap.access_points.find(
            ap => ap.access_point_level === AccessPointsUtil.valuesMap.DEVELOPING
          )
        ].filter(ap => !!ap) // if not all access points are created, filter out undefined ones

        this.common = {
          title: ap.title,
          subject_alignments: ap.subject_alignments,
          grades: ap.grades,
          adaptation_strategies: ap.adaptation_strategies,
          published: ap.published,
          score: ap.score,
          standard_set_refs: ap.standard_set_refs,
          universal: ap.universal,
          author_id: ap.author_id,
          characteristics: ap.characteristics || []
        }

        // populate standards, strategies, and resource info to display more details
        this.standardsForSet = {}
        ap.standard_set_refs.forEach((setRef, index) => {
          let refs = Array.isArray(this.standardsForSet[setRef.standard_set_code]) ? this.standardsForSet[setRef.standard_set_code] : []
          refs.push(setRef.standard_long_ref)
          this.$set(this.standardsForSet, setRef.standard_set_code, refs)
        })
        this.standardsMap = {}
        Object.keys(this.standardsForSet).forEach(setCode =>
          this.populateStandardsMapForSet(setCode)
        )
        await this.populateResourcesMap()
        this.checkAndSetGoalLanguages([ap, ...ap.access_points])
        this.populateStrategiesMap()
        this.populateCharacteristicsMap()

        this.checkDraftStatus()
        this.flushUpdatesCallbacks = [this.checkDraftStatus]
      } catch (err) {
        this.notifyErr('Could not get content', err.message)
      } finally {
        this.loading = false
      }
    },

    async generatePlaceholderAccessPoints() {
      try {
        await generateAccessPoints(this.uuid)
        this.init()
      } catch (err) {
        this.notifyErr('Error generating missing access points', err)
      }
    },

    async checkDraftStatus() {
      // check if this is a draft version
        let draftStatus = await checkAnchorPageForDraft(this.uuid)
        if (draftStatus.draft === true) {
          this.draft = true
        }
        this.$set(this.common, 'published_at', moment(new Date(draftStatus.published_at)).format('M/D/YY H:mm A'))
    },
    async publishDraft() {
      try {
        await publishDraftForAnchorPage(this.uuid)
        this.draft = false
        this.init()
      } catch (err) {
        this.notifyErr('Error saving draft changes', err)
      }
    },
    async deleteDraft() {
      try {
        await deleteDraftForAnchorPage(this.uuid)
        this.draft = false
        this.init()
      } catch (err) {
        this.notifyErr('Error deleting draft changes', err)
      }
    },

    generateObjectivesForAccessPoint(ap) {
      if (!ap.objectives || ap.objectives.length === 0) {
        this.$set(ap, 'objectives', [
          {
            level: ObjectiveLevelValues.QUARTER_1ST,
            title: '',
            text: ''
          },
          {
            level: ObjectiveLevelValues.QUARTER_2ND,
            title: '',
            text: ''
          },
          {
            level: ObjectiveLevelValues.QUARTER_3RD,
            title: '',
            text: ''
          }
        ])

        this.$set(ap, 'assessment_items', {
          [ObjectiveLevelValues.QUARTER_1ST]: [],
          [ObjectiveLevelValues.QUARTER_2ND]: [],
          [ObjectiveLevelValues.QUARTER_3RD]: []
        })
        this.$set(ap, 'objective_resources', {
          [ObjectiveLevelValues.QUARTER_1ST]: [],
          [ObjectiveLevelValues.QUARTER_2ND]: [],
          [ObjectiveLevelValues.QUARTER_3RD]: []
        })
      }
    },

    async clearObjectivesForAccessPoint(ap) {
      if (ap.objectives && ap.objectives.length > 0) {
        try {
          await MessageBox.confirm({
            title: 'Remove Objectives',
            content: 'Are you sure you want to remove objectives for this access point?'
          })

          ap.objectives = []
          ap.objective_resources = {}
          ap.assessment_items = {}

          this.pushUpdate({
            uuids: [ap.uuid],
            updates: { objectives: null, objective_resources: null, assessment_items: null }
          })
        } catch {
          // do nothing if user clicks "cancel" in the MessageBox
        }
      }
    },

    formatObjectivesForUpdate(objectives) {
      return objectives.map(obj => {
        return {
          level: obj.level,
          text: obj.text,
          title: obj.title,
          uuid: obj.uuid,
          resources: obj.resources,
          created_at: obj.created_at,
        }
      })
    },

    linkItemForAccessPointObjective(ap, obj, item) {
      ap.assessment_items[obj.level] = [item]
      this.pushUpdate({ uuids: [ap.uuid], updates: { assessment_items: ap.assessment_items } })
    },
    unlinkItemForAccessPointObjective(ap, obj) {
      ap.assessment_items[obj.level] = []
      this.pushUpdate({ uuids: [ap.uuid], updates: { assessment_items: ap.assessment_items } })
    },

    // Methods for populating options and label maps
    async getSubjectsDomainsGrades() {
      let { subjects, subjectDomainsMap, subjectGradesMap } = await getSubjectsDomainsGrades()
      this.subjectsList = subjects
      this.subjectDomainsMap = subjectDomainsMap
      this.subjectGradesMap = subjectGradesMap
    },
    async populateResourcesMap() {
      let uuids = []
      this.accessPoints.forEach(ap => {
        uuids = uuids.concat(ap.goal_resources)
        if (ap.objective_resources) {
          for (const level in ap.objective_resources) {
            const objResources = ap.objective_resources[level]
            if (objResources && objResources.length > 0) {
              uuids = uuids.concat(objResources)
            }
          }
        }
        if (ap.objectives) {
          for (const objective of ap.objectives) {
            if (objective.resources && objective.resources.length > 0) {
              uuids = uuids.concat(objective.resources)
            }
          }
        }
      })
      if (uuids.length === 0) {
        return
      }

      try {
        let resources = await getResources(uuids.join(','))
        this.resourcesMap = {}
        resources.forEach(res => this.$set(this.resourcesMap, res.uuid, res))
      } catch (err) {
        this.notifyErr('Could not get resources', err)
      }
    },
    checkAndSetGoalLanguages(goals) {
      // checks if the languages on the resources have changed and updates the goal if so
      goals.forEach(goal => {
        const languages = [];
        for (const [key, value] of Object.entries(this.resourcesMap)) {
          if (goal.goal_resources.includes(key)) {
            languages.push(value['language'])
          }
        }

        const currLangs = goal.resource_languages || [];
        const uniqueLangs = [...new Set(languages)];
        if (JSON.stringify(uniqueLangs.sort()) !== JSON.stringify(currLangs.sort())) {
          this.pushUpdate({uuids: [goal.uuid], updates: {resource_languages: uniqueLangs}})
        }
      })
    },
    async populateStrategiesMap() {
      let uuids = this.common.adaptation_strategies
      if (uuids.length === 0) {
        return
      }

      try {
        let strategies = await getStrategiesForUuids(uuids)
        this.strategiesMap = {}
        strategies.forEach(s => this.$set(this.strategiesMap, s.uuid, s))
      } catch (err) {
        this.notifyErr('Could not get strategies', err)
      }
    },
    async populateCharacteristicsMap() {
      let uuids = this.common.characteristics
      if (uuids.length === 0) {
        return
      }

      try {
        let characteristics = await getCharacteristicsForUuids(uuids)
        this.characteristicsMap = {}
        characteristics?.forEach(c => this.$set(this.characteristicsMap, c.Uuid, c))
      } catch (err) {
        this.notifyErr('Could not get characteristics', err)
      }
    },
    async populateStandardsMapForSet(setCode) {
      let refsToFetch = this.standardsForSet[setCode]
      if (refsToFetch.length === 0) {
        return
      }

      try {
        let standards = await getStandardsQuery(setCode, {
          standards: refsToFetch,
          select_fields: [
            'standard_long_ref',
            'standard_short_ref',
            'core_purpose',
            'standard_text',
            'substandard_text'
          ]
        })
        standards.forEach(s => this.$set(this.standardsMap, s.standard_long_ref, s))
      } catch (err) {
        this.notifyErr(`Could not get ${setCode} standards`, err)
      }
    },
    async getStandardSets() {
      try {
        this.validStandardSets = await getValidStandardSets()
      } catch (err) {
        this.notifyErr('Could not get standard sets', err)
      }
    },

    // incremental save methods
    updateStandardsForSet(setCode, stdRefs) {
      this.$set(this.standardsForSet, setCode, stdRefs)
      this.populateStandardsMapForSet(setCode)

      let updatedSetRefs = []
      for (const key in this.standardsForSet) {
        updatedSetRefs = updatedSetRefs.concat(
          this.standardsForSet[key].map(ref => ({
            standard_set_code: key,
            standard_long_ref: ref
          }))
        )
      }
      this.saveCommonData({
        standard_set_refs: updatedSetRefs
      })
    },

    // publish/unpublish
    async togglePublish(flag) {
      if (this.draft && !flag) {
        try {
          await MessageBox.confirm({
            title: 'Unpublish Goal',
            content: '<p class="text-danger"><i class="fa fa-exclamation-triangle" aria-hidden="true"></i> Draft changes will be lost when unpublishing. You must save draft changes before unpublishing if you wish to keep them. Do you wish to proceed?</p>',
            html: true
          })
          await this.deleteDraft()
          this.common.published = flag
          this.saveCommonData({ published: flag }, true)

        } catch {
          // do nothing if user clicks "cancel" in the MessageBox
        }
      } else {
        this.common.published = flag

        // intuitively, user expects publish changes to be saved immediately since it's tied to a button; hence passing true
        this.saveCommonData({ published: flag }, true)
      }
    },

    // common utility methods
    saveCommonData(updates, flushImmediately = false) {
      let uuids = this.accessPoints.map(ap => ap.uuid)
      this.pushUpdate(
        {
          uuids,
          updates
        },
        flushImmediately
      )
    },
    notifyErr(title, content) {
      Notification.notify.error({
        title,
        content,
        duration: 0,
        offsetY: 65
      })
    }
  },
  watch: {
    $route: function() {
      this.init()
    }
  },
  created() {
    this.saveEvents.$on('success', resp => {
      // Update the updated_at field for each access point
      this.accessPoints.forEach((ap, index) => {
        if (resp[ap.uuid]) {
          // Using $set and ... to create new object and reactively update array
          this.$set(this.accessPoints, index, { ...ap, updated_at: resp.updated_at })
        }
      })
    })
    this.saveEvents.$on('error', ({ uuids, err }) => {
      this.notifyErr(`Error saving for ${uuids.join(', ')}`, err)
    })
    this.init()
    this.getSubjectsDomainsGrades()
    this.getStandardSets()
  }
}
</script>

<style scoped>
.edit-anchor-access-container {
  margin-top: -10px;
}
.edit-anchor-access-container >>> .affix {
  z-index: 999;
  background: white;
  width: 100%;
  left: 0;
  border-bottom: 1px solid #ccc;
}
main {
  margin-top: 1em;
}
.quarterly-objectives-header {
  margin-left: calc(0px - var(--spacing-3xl));
}
.quarterly-objectives-header .btn {
  margin-left: var(--spacing-md);
}

.objective-tab {
  margin-top: 0.5em;
}
.draft-section {
  display: contents;
}
.draft-actions {
  margin-left: auto;
}
.btn-warning {
  background-color: #a3661a;
  border-color: #945b16;
}
.btn-warning:hover {
  background-color: #945b16;
}
hr {
  border-color: var(--gray-border-weak);
}
</style>
